import { useEffect, useRef, useState } from 'react';
import { Animated, StyleSheet, View } from 'react-native';
import { formatRestTime } from '@heraion/shared';

import { CuePill } from '../primitives/CuePill';
import { InfoPill, InfoPillBody } from '../primitives/InfoPill';
import { useLiveAnnouncement } from '../primitives/useLiveAnnouncement';
import { useAttentionPulse } from '../attentionCue';
import { useReducedMotion } from '../useReducedMotion';
import type { TFn } from '../i18nTypes';

import type { useTheme } from './theme';

/** Gap between pulses. Frequent on purpose: the offer lives for one rest. */
const PULSE_EVERY_MS = 4_000;
/** The offer is spoken after the bar's "Rest started", so TalkBack does not
 *  cut either sentence short. */
const ANNOUNCE_DELAY_MS = 800;

// The invite to rest longer, on top of the docked rest bar. An offer and
// nothing more: the +m:ss button is the only thing that touches the timer.
//
// Never colour alone: the words, the hourglass glyph and the button all say
// it. The gold ring pulses around the pill; under reduced motion it stays up,
// still, instead (attentionCue.ts: a swap, never silence). The ring is
// `pointerEvents="none"` and outside the pill's box, so it never grows a tap
// target.
export function RestInviteRow({
  sec,
  inviteKey,
  onAccept,
  theme,
  t,
}: {
  /** Seconds the button adds. */
  sec: number;
  /** Changes with every new offer; restarts the pulse. */
  inviteKey: number;
  onAccept: () => void;
  theme: ReturnType<typeof useTheme>;
  t: TFn;
}) {
  const [tipOpen, setTipOpen] = useState(false);
  const reducedMotion = useReducedMotion();
  const pulse = useAttentionPulse({
    active: !reducedMotion,
    enabled: true,
    reducedMotion,
    cueKey: String(inviteKey),
    everyMs: PULSE_EVERY_MS,
  });
  const styles = makeStyles(theme);
  const time = formatRestTime(sec);
  const tipTitle = t('mobile.logger.restInviteTipTitle', {
    defaultValue: 'Why rest longer?',
  });

  // One announcement per offer. Android and web hear it through a region
  // mounted empty (useLiveAnnouncement); iOS, where live regions are inert,
  // gets it pushed, the same split CuePill makes.
  const spoken = t('mobile.logger.restInviteA11y', {
    time,
    defaultValue: 'Rest a bit longer? You can add {{time}}.',
  });
  const spokenRef = useRef(spoken);
  spokenRef.current = spoken;
  const { speak, region } = useLiveAnnouncement();
  useEffect(() => {
    const id = setTimeout(() => {
      speak(spokenRef.current);
    }, ANNOUNCE_DELAY_MS);
    return () => clearTimeout(id);
  }, [inviteKey, speak]);

  return (
    <View style={styles.block}>
      <View style={styles.row}>
        <View style={styles.pillWrap}>
          <Animated.View
            pointerEvents="none"
            style={[styles.ring, { opacity: reducedMotion ? 1 : pulse }]}
          />
          <CuePill
            theme={theme}
            tone="warn"
            icon="hourglass"
            label={t('mobile.logger.restInviteLabel', {
              defaultValue: 'Rest a bit longer?',
            })}
            action={{
              label: t('mobile.logger.restInviteAdd', {
                time,
                defaultValue: '+{{time}}',
              }),
              accessibilityLabel: t('mobile.logger.restInviteAddA11y', {
                time,
                defaultValue: 'Add {{time}} to the rest',
              }),
              onPress: onAccept,
            }}
          />
        </View>
        <InfoPill
          theme={theme}
          open={tipOpen}
          onToggle={() => setTipOpen((o) => !o)}
          a11yLabel={tipTitle}
        />
      </View>
      {region}
      {tipOpen ? (
        <InfoPillBody
          theme={theme}
          title={tipTitle}
          text={t('mobile.logger.restInviteTipBody')}
        />
      ) : null}
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r } = theme;
  return StyleSheet.create({
    // s5, not less: the +m:ss button's 8pt bottom slop reaches 5pt past the
    // pill, and the rest controls below claim 8pt above themselves. 16 keeps
    // the two touch zones apart.
    block: { marginBottom: s.s5 },
    // s3: the button's 8pt right slop ends at the pill's edge (its own s3
    // padding), and the ?'s 8pt slop must start no sooner.
    row: { flexDirection: 'row', alignItems: 'center', gap: s.s3 },
    pillWrap: { flex: 1 },
    // 2pt outside the pill, so the ring frames it instead of covering its
    // border. The warn token, the same family the pill is tinted from.
    ring: {
      position: 'absolute',
      top: -2,
      bottom: -2,
      left: -2,
      right: -2,
      borderWidth: 2,
      borderRadius: r.r2 + 2,
      borderColor: p.warn,
    },
  });
}
